// Phase 89B.1 — small reusable UI primitives for the Match Performance workspace.
import React, { useMemo, useState } from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Search, Plus, X } from 'lucide-react';
import { ASSESSMENTS } from './constants';

export const TagPicker = ({ open, onOpenChange, tags, selected, onToggle }) => {
  const [q, setQ] = useState('');
  const grouped = useMemo(() => {
    const g = {};
    (tags || []).forEach(t => {
      if (q && !t.label.toLowerCase().includes(q.toLowerCase())) return;
      (g[t.category] = g[t.category] || []).push(t);
    });
    return g;
  }, [tags, q]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="tag-picker-dialog">
        <DialogHeader><DialogTitle>Development Tags</DialogTitle></DialogHeader>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2 top-2.5 text-gray-400" />
          <Input className="pl-8" placeholder="Search tags…" value={q}
                 onChange={e => setQ(e.target.value)} data-testid="tag-search" />
        </div>
        <div className="max-h-[50vh] overflow-y-auto space-y-3 py-1">
          {Object.keys(grouped).length === 0 && (
            <p className="text-sm text-gray-500 py-4 text-center">No matching tags.</p>
          )}
          {Object.entries(grouped).map(([cat, list]) => (
            <div key={cat}>
              <p className="text-xs font-semibold uppercase text-gray-400 mb-1">{cat}</p>
              <div className="flex flex-wrap gap-1.5">
                {list.map(t => {
                  const on = selected.includes(t.id);
                  return (
                    <button key={t.id} type="button" onClick={() => onToggle(t.id)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${on
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-emerald-400'}`}
                      data-testid={`tag-option-${t.id}`}>
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} data-testid="tag-picker-done">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const EvidenceRows = ({ items, setItems, testidPrefix = 'evidence' }) => (
  <div className="space-y-2">
    {items.map((ev, i) => (
      <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`${testidPrefix}-row-${i}`}>
        <Input className="col-span-3" placeholder="Source" value={ev.source}
               onChange={e => setItems(items.map((x, j) => j === i ? { ...x, source: e.target.value } : x))}
               data-testid={`${testidPrefix}-source-${i}`} />
        <Input className="col-span-2" placeholder="67:39" value={ev.timestamp}
               onChange={e => setItems(items.map((x, j) => j === i ? { ...x, timestamp: e.target.value } : x))}
               data-testid={`${testidPrefix}-ts-${i}`} />
        <Input className="col-span-3" placeholder="Label" value={ev.label}
               onChange={e => setItems(items.map((x, j) => j === i ? { ...x, label: e.target.value } : x))}
               data-testid={`${testidPrefix}-label-${i}`} />
        <Input className="col-span-3" placeholder="URL (optional)" value={ev.url}
               onChange={e => setItems(items.map((x, j) => j === i ? { ...x, url: e.target.value } : x))}
               data-testid={`${testidPrefix}-url-${i}`} />
        <button type="button" className="col-span-1 text-gray-400 hover:text-red-500"
                onClick={() => setItems(items.filter((_, j) => j !== i))}
                data-testid={`${testidPrefix}-remove-${i}`}>
          <X className="w-4 h-4" />
        </button>
      </div>
    ))}
    <Button type="button" size="sm" variant="outline" onClick={() => setItems([...items, { source: 'Veo', timestamp: '', url: '', clip_url: '', label: '' }])}
            data-testid={`${testidPrefix}-add`}>
      <Plus className="w-3.5 h-3.5 mr-1" /> Add evidence
    </Button>
  </div>
);

export const AssessmentPicker = ({ value, onChange }) => (
  <div className="flex gap-2 flex-wrap" data-testid="assessment-picker">
    {ASSESSMENTS.map(a => {
      const Icon = a.icon; const on = value === a.value;
      return (
        <button key={a.value} type="button" onClick={() => onChange(a.value)}
          className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border transition-all ${on
            ? a.cls + ' ring-2 ring-offset-1 ring-current' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}
          data-testid={`assessment-${a.value}`}>
          <Icon className="w-3.5 h-3.5" /> {a.label}
        </button>
      );
    })}
  </div>
);

export const TagChips = ({ ids, tagsById, onRemove }) => (
  <div className="flex flex-wrap gap-1.5">
    {ids.length === 0 && <span className="text-xs text-gray-400">No tags selected</span>}
    {ids.map(id => (
      <Badge key={id} className="bg-emerald-100 text-emerald-800 border-emerald-200 gap-1" data-testid={`chip-${id}`}>
        {tagsById[id]?.label || id}
        {onRemove && (
          <button type="button" onClick={() => onRemove(id)} className="hover:text-red-600">
            <X className="w-3 h-3" />
          </button>
        )}
      </Badge>
    ))}
  </div>
);
